import { useEffect, useState, useMemo } from 'react';
import userApis from 'features/user/services/apis';
import dayjs from 'dayjs';
import Table from 'components/Table';
import { Avatar } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { ColumnsType } from 'antd/es/table';
import { debounce } from 'lodash';

type ITimeTrackerTableProps = {
  date?: string;
  staffId?: string;
};

type ITimeTracker = {
  staffName: string;
  avatar: string;
  time: string;
  type: 'CLOCK_IN' | 'CLOCK_OUT';
  clockInTime: string;
  clockOutTime: string;
  workingTime: number;
};

const TimeTrackerTable = ({ date, staffId }: ITimeTrackerTableProps) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ITimeTracker[]>([]);

  const fetchData = async (date?: string, staffId?: string) => {
    try {
      setLoading(true);
      const _date = date ? date : dayjs().format('MM-DD-YYYY');
      const res = await userApis.getTimeTracker(_date, staffId || '');
      if (res?.data?.data?.content) {
        setData(res.data.data.content);
      } else {
        setData([]);
      }
    } catch (error) {
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetchData = useMemo(() => debounce(fetchData, 300), []);

  useEffect(() => {
    if (!date || !dayjs(date).isValid()) return;
    debouncedFetchData(date, staffId);
    return () => {
      debouncedFetchData.cancel();
    };
  }, [date, staffId, debouncedFetchData]);

  const columns: ColumnsType<ITimeTracker> = [
    {
      title: 'Staff',
      dataIndex: 'staffName',
      key: 'staffName',
      align: 'left',
      render: (text: string, record: ITimeTracker) => {
        return (
          <span>
            <Avatar icon={<UserOutlined />} src={record?.avatar || undefined} size={40} />
            <span> {text}</span>
          </span>
        );
      },
    },
    {
      title: 'Clock In Time',
      dataIndex: 'clockInTime',
      key: 'clockInTime',
      align: 'left',
      render: (text: string) => {
        return <span>{text ? text : '--'}</span>;
      },
    },
    {
      title: 'Clock Out Time',
      dataIndex: 'clockOutTime',
      key: 'clockOutTime',
      align: 'left',
      render: (text: string) => {
        return <span>{text ? text : '--'}</span>;
      },
    },
    {
      title: 'Working Time',
      dataIndex: 'workingTime',
      key: 'workingTime',
      align: 'left',
      render: (text: string) => {
        return <span>{text ? text + 'h' : '--'}</span>;
      },
    },
  ];

  return (
    <Container className='v2'>
      <Table
        rowKey={(record, index) => {
          const key = `${record.staffName}-${record.clockInTime}-${record.clockOutTime}-${record.workingTime}-${index}`;
          return key;
        }}
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={{
          total: data.length,
          pageSize: 1000,
          current: 1,
          showSizeChanger: false,
        }}
      />
    </Container>
  );
};

export default TimeTrackerTable;

const Container = styled.div`
  width: 100%;
  max-height: calc(80vh - 92px);
  overflow: auto;
  .ant-table-cell {
    padding: 0.5rem !important;
  }
  th.ant-table-cell {
    border-right: 1px solid #fff;
    text-align: center !important;
    &:nth-last-child(1) {
      border-right: none;
    }
    padding: 14px 0.5rem !important;
  }
  &.v2 {
    .ant-table-wrapper .ant-pagination {
      position: sticky;
      bottom: 0;
      border: 0;
      .ant-pagination-options {
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);

        .ant-select.ant-pagination-options-size-changer {
          height: 40px;
          .ant-select-selector {
            padding: 0 20px;
          }
        }

        .ant-select .ant-select-arrow {
          color: #1d2129;
          font-size: 18px;
          transform: scaleX(1.1) scaleY(0.9);
        }
        .ant-select .ant-select-selector {
          background: #ffffff;
          border: 1px solid #86909c;
          .ant-select-selection-item-content {
            color: #1d2129;
            font-family: Poppins;
            font-size: 16px;
            font-style: normal;
            font-weight: 500;
            line-height: normal;
          }
        }
        .ant-select .ant-select-selector {
          background: #ffffff;
          border: 1px solid #86909c;
          .ant-select-selection-placeholder,
          .ant-select-selection-search-input,
          .ant-select-selection-item {
            color: rgb(29, 33, 41);
            font-family: Poppins;
            font-size: 16px;
            font-style: normal;
            font-weight: 500;
            line-height: normal;
          }
        }
      }
    }

    .ant-table-wrapper .ant-table .ant-table-content {
      max-height: unset;
      overflow: unset;
      .ant-table-thead th.ant-table-cell,
      .ant-table-thead td.ant-table-cell {
        height: 48px;
        padding: 0px 8px;
        gap: 10px;
        align-self: stretch;
        border-bottom: 1px solid #ccd4dc;
        background: #6fabb6;
        color: #fff;
        font-family: Poppins;
        font-size: 16px;
        font-style: normal;
        font-weight: 600;
        line-height: normal;
        text-transform: capitalize;
        text-align: left !important;
        position: sticky;
        top: 0;
        &::before {
          width: 1.5px;
          height: 100%;
        }
      }

      .ant-table-tbody td.ant-table-cell {
        .BODY_1,
        .CONTENT_2 {
          color: #1d2129;
          font-family: Poppins;
          font-size: 16px;
          font-style: normal;
          font-weight: 500;
          line-height: normal;
        }
      }

      .ant-table-tbody .ant-table-row:nth-child(even) td {
        background: #e4f4f6;
        border-bottom: 1px solid #e4f4f6;
      }
    }
  }
`;
